"use client";

import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useConsultation } from "../../../../context/ConsultationContext";
import Navbar from "../../../../components/navbar";
import Footer from "../../../../components/footer";
import ConsultationHeader from "../components/ConsultationHeader";
import styles from "../css/diagnosis-icd10.module.css";
import {
  getConsultationFull,
} from "../../../../api/clinic/consultationApi.js";

export default function DiagnosisICD10() {
  const navigate = useNavigate();
  const { consultationData, previousStep, nextStep, updateConsultationData, getStepDraft, setStepDraft } = useConsultation();
  const [saving, setSaving] = useState(false);

  const draftKey = "diagnosisICD10";
  
  const defaultFormData = {
    searchQuery: "",
    selectedDiagnoses: []
  };

  const [formData, setFormData] = useState(() => getStepDraft(draftKey, defaultFormData));

  // Mock ICD10 data - in real app this would come from API
  const [icd10Codes] = useState([
    { code: "A01.0", description: "Typhoid fever" },
    { code: "A01.1", description: "Paratyphoid fever A" },
    { code: "A01.2", description: "Paratyphoid fever B" },
    { code: "A01.3", description: "Paratyphoid fever C" },
    { code: "A01.4", description: "Paratyphoid fever, unspecified" },
    { code: "A02.0", description: "Salmonella enteritis" },
    { code: "A02.1", description: "Salmonella sepsis" },
    { code: "A02.2", description: "Localized salmonella infection" },
    { code: "A02.3", description: "Salmonella infection, unspecified" },
    { code: "A02.8", description: "Other specified salmonella infections" },
    { code: "A02.9", description: "Salmonella infection, unspecified" },
    { code: "A03.0", description: "Shigellosis due to Shigella dysenteriae" },
    { code: "A03.1", description: "Shigellosis due to Shigella flexneri" },
    { code: "A03.2", description: "Shigellosis due to Shigella boydii" },
    { code: "A03.3", description: "Shigellosis due to Shigella sonnei" },
    { code: "A03.8", description: "Other shigellosis" },
    { code: "A03.9", description: "Shigellosis, unspecified" },
    { code: "A04.0", description: "Enteropathogenic Escherichia coli infection" },
    { code: "A04.1", description: "Enterotoxigenic Escherichia coli infection" },
    { code: "A04.2", description: "Enteroinvasive Escherichia coli infection" }
  ]);

  const filteredCodes = formData.searchQuery ? icd10Codes.filter(item => 
    item.code.toLowerCase().includes(formData.searchQuery.toLowerCase()) ||
    item.description.toLowerCase().includes(formData.searchQuery.toLowerCase())
  ) : [];

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => {
      const next = { ...prev, [name]: value };
      setStepDraft(draftKey, next);
      return next;
    });
  };

  const addDiagnosis = (diagnosis) => {
    setFormData((prev) => {
      const next = { ...prev, selectedDiagnoses: [...prev.selectedDiagnoses, diagnosis] };
      setStepDraft(draftKey, next);
      return next;
    });
  };

  const removeDiagnosis = (index) => {
    setFormData((prev) => {
      const next = { ...prev, selectedDiagnoses: prev.selectedDiagnoses.filter((_, i) => i !== index) };
      setStepDraft(draftKey, next);
      return next;
    });
  };

  const handlePrevious = () => {
    previousStep();
    navigate("/customer/clinic/specialized-tests");
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const id = consultationData?.consultationId;
        if (!id) return;
        const full = await getConsultationFull(id);
        const items = Array.isArray(full?.consultation_diagnosis) ? full.consultation_diagnosis : [];
        if (cancelled) return;

        const existingDraft = getStepDraft(draftKey, null);
        if (existingDraft) return;

        const next = {
          ...defaultFormData,
          selectedDiagnoses: items.map((d) => ({
            code: d.icd10code || "",
            description: d.icd10description || "",
            isprimary: !!d.isprimary,
          })),
        };

        setFormData(next);
        setStepDraft(draftKey, next);
      } catch (e) {
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [consultationData?.consultationId]);

  const handleNext = async () => {
    try {
      setSaving(true);
      nextStep();
      navigate("/customer/clinic/problem-list");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className={styles.pageWrapper}>
      <Navbar />
      
      <main className={styles.mainContent}>
        <div className={styles.container}>
          <ConsultationHeader 
            variant="step"
            currentStep={6}
            totalSteps={10}
            stepTitle="Diagnosis & ICD10"
            stepDetails="Step 6 of 10 · Maria van der Merwe · General Consultation"
            onPrevious={handlePrevious}
            onNext={handleNext}
            nextDisabled={saving}
          />

          <div className={styles.formContainer}>
            {/* ICD10 CODE LOOKUP */}
            <div className={styles.sectionCard}>
              <h3 className={styles.blueSubHeader}>ICD10 CODE LOOKUP</h3>
              
              {/* Search Bar */}
              <div className={styles.searchContainer}>
                <input
                  type="text"
                  name="searchQuery"
                  value={formData.searchQuery}
                  onChange={handleInputChange}
                  placeholder="Search ICD10 codes or descriptions..."
                  className={styles.searchInput}
                />
              </div>

              {/* Results List */}
              <div className={styles.resultsContainer}>
                {filteredCodes.length === 0 && formData.searchQuery ? (
                  <div className={styles.noResultsMessage}>
                    <p>No results found for "{formData.searchQuery}"</p>
                  </div>
                ) : (
                  <div className={styles.resultsList}>
                    {filteredCodes.map((item, index) => (
                      <div key={index} className={styles.resultItem}>
                        <div className={styles.resultContent}>
                          <span className={styles.code}>{item.code}</span>
                          <span className={styles.description}>{item.description}</span>
                        </div>
                        <button
                          type="button"
                          className={styles.addButton}
                          onClick={() => addDiagnosis(item)}
                        >
                          +
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* SELECTED DIAGNOSES */}
            <div className={styles.sectionCard}>
              <h3 className={styles.blueSubHeader}>SELECTED DIAGNOSES</h3>
              
              <div className={styles.selectedList}>
                {formData.selectedDiagnoses.length === 0 ? (
                  <p className={styles.noSelections}>No diagnoses selected yet</p>
                ) : (
                  formData.selectedDiagnoses.map((diagnosis, index) => (
                    <div key={index} className={styles.selectedItem}>
                      <div className={styles.selectedContent}>
                        <span className={styles.code}>{diagnosis.code}</span>
                        <span className={styles.description}>{diagnosis.description}</span>
                      </div>
                      <button
                        type="button"
                        className={styles.removeButton}
                        onClick={() => removeDiagnosis(index)}
                      >
                        ×
                      </button>
                    </div>
                  ))
                )}
              </div>
            </div>
          </div>
        </div>
      </main>
      
      <Footer />
    </div>
  );
}
